<template>
    <v-container class="mt-8 pb-12" style="max-width: 1100px;">
        <v-btn text class="text-none mb-2 ml-n3" :to="{ name: 'Companies' }">
            <v-icon left>mdi-arrow-left</v-icon>
            All companies
        </v-btn>

        <v-skeleton-loader v-if="!company" type="card, list-item-two-line@3" />

        <template v-else>
            <v-card outlined class="pa-5 mb-6">
                <div class="d-flex align-start flex-wrap">
                    <v-icon color="primary" size="36" class="mr-3">mdi-domain</v-icon>
                    <div style="min-width: 0; flex: 1;">
                        <div class="d-flex align-center">
                            <h1 class="text-h5 font-weight-bold mr-3">{{ company.name }}</h1>
                            <v-chip small outlined>{{ company.status || 'ACTIVE' }}</v-chip>
                        </div>
                        <p class="grey--text text--darken-1 mt-2 mb-0">{{ company.description || 'No description' }}</p>
                    </div>
                    <div class="d-flex flex-column flex-sm-row mt-2" style="gap: 8px;">
                        <v-btn v-if="canEditDetails" outlined color="primary" class="text-none" @click="editDialog = true">
                            <v-icon left>mdi-pencil</v-icon>
                            Edit Company
                        </v-btn>
                        <v-btn color="primary" class="text-none"
                            :to="{ name: 'ViewProject', params: { id: company.id } }">
                            <v-icon left>mdi-view-dashboard-outline</v-icon>
                            Open Project Dashboard
                        </v-btn>
                    </div>
                </div>
            </v-card>

            <div class="d-flex align-center mb-3">
                <h2 class="text-h6 font-weight-bold">Members ({{ members.length }})</h2>
                <v-spacer />
                <v-btn color="primary" class="text-none" @click="addMemberDialog = true">
                    <v-icon left>mdi-plus</v-icon>
                    Add Member
                </v-btn>
            </div>

            <v-text-field v-model="memberSearch" prepend-inner-icon="mdi-magnify"
                placeholder="Search members by name, position or email" outlined dense clearable hide-details
                class="mb-3" />

            <v-card outlined>
                <v-list v-if="filteredMembers.length" two-line class="py-0">
                    <template v-for="(member, i) in filteredMembers">
                        <v-divider v-if="i > 0" :key="`d-${member.id}`" />
                        <v-list-item :key="member.id">
                            <v-list-item-avatar color="primary" class="white--text">
                                {{ initials(member) }}
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title class="font-weight-medium">
                                    {{ displayName(member) }}
                                    <v-chip v-if="isManager(member)" x-small color="primary" class="ml-2">
                                        <v-icon left x-small>mdi-shield-account</v-icon>
                                        Manager
                                    </v-chip>
                                </v-list-item-title>
                                <v-list-item-subtitle>{{ member.position_name || member.email || 'Team Member' }}</v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action class="flex-row align-center">
                                <v-btn small text color="primary" class="text-none mr-2"
                                    :loading="togglingId === accessFor(member).id"
                                    @click="toggleManager(member)">
                                    {{ isManager(member) ? 'Remove manager' : 'Make manager' }}
                                </v-btn>
                                <v-btn icon small @click="confirmRemove(member)">
                                    <v-icon color="error">mdi-account-remove</v-icon>
                                </v-btn>
                            </v-list-item-action>
                        </v-list-item>
                    </template>
                </v-list>
                <div v-else class="pa-8 text-center grey--text">
                    {{ memberSearch ? 'No members match your search' : 'No members yet' }}
                </div>
            </v-card>
        </template>

        <EditCompanyDialog v-model="editDialog" :company="company || {}" @company-updated="onCompanyUpdated" />
        <AddMemberDialog v-model="addMemberDialog" :company-id="companyId"
            :company-name="company ? company.name : ''" :existing-member-ids="members.map(m => m.id)"
            @members-added="onMembersAdded" />

        <v-dialog v-model="removeDialog" max-width="480">
            <v-card>
                <v-card-title class="headline red white--text">
                    <v-icon left color="white">mdi-account-remove</v-icon>
                    Remove Member
                </v-card-title>
                <v-card-text class="pa-6">
                    <p class="text-body-1 mb-1">Remove this member from {{ company ? company.name : 'the company' }}?</p>
                    <p v-if="memberToRemove" class="font-weight-bold mb-1">{{ displayName(memberToRemove) }}</p>
                    <p class="text-caption grey--text mb-0">This revokes their access to this company and its resources.</p>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-spacer />
                    <v-btn text @click="removeDialog = false">Cancel</v-btn>
                    <v-btn color="red" dark :loading="removing" @click="removeMember">Remove</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import EditCompanyDialog from '@/components/projects/dialogs/EditCompanyDialog.vue';
import { canManageCompanyPage, canEditCompanyDetails } from './companyAccess';
import AddMemberDialog from '@/components/projects/dialogs/AddMemberDialog.vue';

export default {
    name: 'CompanyDetailsComponent',
    components: { EditCompanyDialog, AddMemberDialog },
    inject: ['companies', 'users', 'refreshCompanies', 'refreshUsers'],
    data() {
        return {
            editDialog: false,
            addMemberDialog: false,
            removeDialog: false,
            memberToRemove: null,
            removing: false,
            togglingId: null,
            memberSearch: '',
        };
    },
    computed: {
        ...mapGetters({ me: 'auth/get_user' }),
        canEditDetails() {
            return canEditCompanyDetails(this.me);
        },
        allowed() {
            return canManageCompanyPage(this.me, this.users(), this.companyId);
        },
        companyId() {
            return parseInt(this.$route.params.id);
        },
        company() {
            const list = this.companies();
            return Array.isArray(list) ? list.find(c => c.id === this.companyId) || null : null;
        },
        members() {
            const list = this.users();
            if (!Array.isArray(list)) return [];
            // Managers first, then alphabetical
            return list
                .filter(u => Array.isArray(u.access) && u.access.some(a => a.company_id === this.companyId))
                .sort((a, b) =>
                    (this.isManager(b) - this.isManager(a)) ||
                    this.displayName(a).localeCompare(this.displayName(b)));
        },
        filteredMembers() {
            const q = (this.memberSearch || '').trim().toLowerCase();
            if (!q) return this.members;
            return this.members.filter(m =>
                [this.displayName(m), m.position_name, m.email]
                    .some(v => (v || '').toLowerCase().includes(q)));
        },
    },
    watch: {
        // The users list may still be loading on first render, so re-check when it arrives.
        allowed: 'enforceAccess',
    },
    mounted() {
        this.enforceAccess();
    },
    methods: {
        enforceAccess() {
            const list = this.users();
            if (!Array.isArray(list) || !list.length) return; // not loaded yet
            if (!this.allowed) {
                this.$toast.error('You can only open companies you manage');
                this.$router.replace({ name: 'Companies' });
            }
        },
        displayName(u) {
            return u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim();
        },
        accessFor(u) {
            return (u.access || []).find(a => a.company_id === this.companyId) || {};
        },
        isManager(u) {
            return !!this.accessFor(u).is_manager;
        },
        async toggleManager(member) {
            const access = this.accessFor(member);
            if (!access.id) return;
            const next = !access.is_manager;
            this.togglingId = access.id;
            try {
                const response = await this.$axios.post(`api/companies/set_company_manager/${access.id}`, { is_manager: next });
                if (response.data.response) {
                    await this.refreshUsers();
                    this.$toast.success(next
                        ? `${this.displayName(member)} is now a manager`
                        : `${this.displayName(member)} is no longer a manager`);
                } else {
                    this.$toast.error(response.data.message || 'Failed to update manager');
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Failed to update manager');
            } finally {
                this.togglingId = null;
            }
        },
        initials(u) {
            return this.displayName(u).split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');
        },
        async onCompanyUpdated() {
            await this.refreshCompanies();
            this.$toast.success('Company updated successfully');
        },
        async onMembersAdded() {
            await this.refreshUsers();
            await this.refreshCompanies();
        },
        confirmRemove(member) {
            this.memberToRemove = member;
            this.removeDialog = true;
        },
        async removeMember() {
            const access = this.memberToRemove?.access?.find(a => a.company_id === this.companyId);
            if (!access) {
                this.$toast.error('Invalid access ID');
                return;
            }
            this.removing = true;
            try {
                const response = await this.$axios.delete(`/api/companies/delete_company_access/${access.id}`);
                if (response.data.response || response.data.success) {
                    await this.refreshCompanies();
                    await this.refreshUsers();
                    this.$toast.success(`${this.displayName(this.memberToRemove)} removed from company`);
                } else {
                    this.$toast.error('Failed to remove member from company');
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Failed to remove member');
            } finally {
                this.removing = false;
                this.removeDialog = false;
                this.memberToRemove = null;
            }
        },
    },
};
</script>
